import React from "react";
import Head from "next/head";
import { useRouter } from "next/router";

import {
  getAlternateUrl,
  getCanonicalUrl,
  parseQueryString,
  serializeQueryString,
} from "@catchoftheday/utilities";

function getCanonicalQueryParams(
  queryString: string,
  config: QueryParamConfig
) {
  const queryObject = parseQueryString(queryString);
  const modifiedQueryObject = Object.entries(config).reduce(
    (acc, [key, value]) => {
      if (typeof value === "boolean") {
        acc[key] = queryObject[key];
      } else if (typeof value === "string") {
        acc[key] = value;
      } else if (typeof value === "function") {
        acc[key] = value({ location, value: queryObject[key] });
      }

      return acc;
    },
    {} as Record<string, string>
  );

  const queryParamsString = serializeQueryString(modifiedQueryObject);
  return queryParamsString ? `?${queryParamsString}` : "";
}

type QueryParamConfig = Record<
  string,
  boolean | string | ((props: { location: Location; value?: string }) => string)
>;

export type SEOProps = {
  origin: string;
  ssr?: boolean;
  alternateTags?: {
    disabled?: boolean;
    useTrailingSlash?: (props: { url: URL }) => boolean;
    includeQueryParams?: (props: { url: URL }) => boolean;
  };
  canonicalTag?: {
    disabled?: boolean;
    useTrailingSlash?: (props: { url: URL }) => boolean;
    queryParams?: QueryParamConfig;
  };
};

export const SEO = ({
  ssr = true,
  origin,
  alternateTags,
  canonicalTag,
}: SEOProps) => {
  const { asPath } = useRouter();

  if (!ssr && typeof window === "undefined") {
    return null;
  }

  const parsedUrl = new URL(`${origin}${asPath}`);

  let canonicalUrl: string | undefined;
  if (!canonicalTag?.disabled) {
    const canonicalQueryParams = getCanonicalQueryParams(
      parsedUrl.search,
      canonicalTag?.queryParams || {}
    );
    if (
      canonicalTag?.useTrailingSlash?.({ url: parsedUrl }) &&
      !parsedUrl.pathname.match(/\/$/)
    ) {
      parsedUrl.pathname = `${parsedUrl.pathname}/`;
    }
    canonicalUrl = `${parsedUrl.origin}${parsedUrl.pathname}${canonicalQueryParams}`;
  }

  let alternateUrl: string | undefined;
  if (!alternateTags?.disabled) {
    const includeQueryParams = alternateTags?.includeQueryParams?.({
      url: parsedUrl,
    });
    alternateUrl = `${parsedUrl.origin}${parsedUrl.pathname}${
      includeQueryParams ? parsedUrl.search : ""
    }`;
  }

  return (
    <Head>
      {canonicalUrl && (
        <link
          key="canonical"
          rel="canonical"
          href={getCanonicalUrl(canonicalUrl)}
        />
      )}
      {alternateUrl && (
        <>
          <link
            key="alternate-nz"
            rel="alternate"
            href={getAlternateUrl(alternateUrl, "nz")}
            hrefLang="en-NZ"
          />
          <link
            key="alternate-au"
            rel="alternate"
            href={getAlternateUrl(alternateUrl, "au")}
            hrefLang="en-AU"
          />
          <link
            key="alternate-mobile"
            rel="alternate"
            media="only screen and (max-width: 640px)"
            href={getAlternateUrl(alternateUrl, "mobile")}
          />
        </>
      )}
      <meta
        name="viewport"
        content="width=device-width, initial-scale=1, maximum-scale=1"
      ></meta>
      <meta property="fb:app_id" content="282892088568315" />
      <meta property="fb:page_id" content="90383230869" />

      {/* Access to and use of BrightEdge AutoPilot - Self Connecting Pages is governed by the
			Infrastructure Product Terms located at: www.brightedge.com/infrastructure-product-terms.*/}
      <script
        src={`https://cdn.bc0a.com/autopilot/f00000000244369/autopilot_sdk.js`}
      />
    </Head>
  );
};
